import { useEffect, useId, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouteContext } from '@tanstack/react-router'
import { useDebounce, useLatest } from 'ahooks'
import { Alert, App, Button, Descriptions, Input, Radio, Select, Upload } from 'antd'
import dayjs from 'dayjs'
import { UploadCloud } from 'lucide-react'
import { ocrFileSchema } from '@super-mega/server/contracts/ocr'
import type { DocumentChoice } from '@super-mega/server/contracts/order'
import { LicenseDocumentPreview } from '@/features/license/index.ts'
import { useTRPC } from '@/trpc/react.ts'

export type DocumentDisplay = { name?: string | null; number?: string | null; legalRepresentative?: string | null }

function requireDocument<T>(record: T, description: string): NonNullable<T> {
  if (record === null || record === undefined) {
    throw new Error(description)
  }
  return record
}

export function EnterpriseDocumentPicker({
  kind,
  value,
  onChange,
  onReadyChange,
  disabled,
  current
}: {
  kind: 'company' | 'identity'
  value?: DocumentChoice
  current?: DocumentDisplay
  onChange: (choice: DocumentChoice, display?: DocumentDisplay, linkedIdentityId?: number | null) => void
  onReadyChange: (ready: boolean) => void
  disabled?: boolean
}) {
  const creditCodeInputId = useId()
  const trpc = useTRPC()
  const cache = useQueryClient()
  const { message } = App.useApp()
  const { currentSession } = useRouteContext({ from: '/_authenticated' })
  const canRead = currentSession.isSuperAdmin || currentSession.permissions.includes(`${kind}.read`)
  const canRecognize =
    currentSession.isSuperAdmin ||
    (currentSession.permissions.includes(kind === 'company' ? 'ocr.businessLicense' : 'ocr.idCard') &&
      currentSession.permissions.some(
        (permission) => permission === `${kind}.create` || permission === `${kind}.update`
      ))
  const [localMode, setMode] = useState<DocumentChoice['mode']>(value?.mode ?? 'none')
  const mode = value && value.mode !== 'none' ? value.mode : localMode
  const canLookup =
    currentSession.isSuperAdmin ||
    (currentSession.permissions.includes('company.lookup') &&
      currentSession.permissions.some(
        (permission) => permission.startsWith('company.') && !permission.endsWith('.read')
      ))
  const [creditCode, setCreditCode] = useState('')
  const [keyword, setKeyword] = useState('')
  const [file, setFile] = useState<File>()
  const [url, setUrl] = useState<string>()
  const [display, setDisplay] = useState<DocumentDisplay>()
  const [error, setError] = useState<string>()
  const [resolving, setResolving] = useState(false)
  const generation = useRef(0)
  const readyCallback = useLatest(onReadyChange)
  useEffect(
    () => () => {
      generation.current += 1
      readyCallback.current(true)
    },
    [readyCallback]
  )
  useEffect(
    () => () => {
      if (url) {
        URL.revokeObjectURL(url)
      }
    },
    [url]
  )
  const search = useDebounce(keyword.trim(), { wait: 300 })
  const companies = useQuery({
    ...trpc.company.search.queryOptions({ keyword: search }),
    enabled: canRead && kind === 'company' && mode === 'existing'
  })
  const identities = useQuery({
    ...trpc.identity.search.queryOptions({ keyword: search }),
    enabled: canRead && kind === 'identity' && mode === 'existing'
  })
  const recognition = useMutation(trpc.order.subjects.previewDocument.mutationOptions())
  const selectedId = value?.mode === 'existing' ? value.id : 0
  const selectedCompany = useQuery({
    ...trpc.company.reusable.queryOptions({ id: selectedId }),
    enabled: canRead && kind === 'company' && selectedId > 0
  })
  const selectedIdentity = useQuery({
    ...trpc.identity.reusable.queryOptions({ id: selectedId }),
    enabled: canRead && kind === 'identity' && selectedId > 0
  })
  let candidateDisplay = display
  if (!candidateDisplay && kind === 'company' && selectedCompany.data) {
    candidateDisplay = {
      name: selectedCompany.data.name,
      number: selectedCompany.data.unifiedSocialCreditCode,
      legalRepresentative: selectedCompany.data.legalRepresentative
    }
  } else if (!candidateDisplay && kind === 'identity' && selectedIdentity.data) {
    candidateDisplay = { name: selectedIdentity.data.name, number: selectedIdentity.data.idNumber }
  }
  const retainingCurrent = mode === 'none' && Boolean(current)
  const shownDisplay = retainingCurrent ? current : candidateDisplay
  const busy = disabled || resolving || recognition.isPending
  const title = kind === 'company' ? '营业执照' : '身份证人像面'
  let uploadButtonText = `点击上传${title}`
  if (file) {
    uploadButtonText = '更换图片'
  }
  if (recognition.isPending) {
    uploadButtonText = '正在识别…'
  }
  const options =
    kind === 'company'
      ? companies.data?.map((row) => ({ value: row.id, label: `${row.name} · ${row.unifiedSocialCreditCode}` }))
      : identities.data?.map((row) => ({
          value: row.id,
          label: `${row.name} · ${row.idNumber.replace(/^(.{3}).*(.{4})$/, '$1***********$2')}`
        }))

  const recognize = async (source: File) => {
    const ticket = ++generation.current
    onReadyChange(false)
    setError(undefined)
    const body = new FormData()
    body.set('file', source)
    body.set('kind', kind)
    try {
      const result = await recognition.mutateAsync(body)
      if (generation.current !== ticket) {
        return
      }
      if (result.kind !== kind) {
        setError('识别返回的证件类型不一致，请重试')
        return
      }
      const data = result.result
      const fields: DocumentDisplay = {
        name: data.name,
        number: 'unifiedSocialCreditCode' in data ? data.unifiedSocialCreditCode : data.idNumber,
        legalRepresentative: 'legalRepresentative' in data ? data.legalRepresentative : undefined
      }
      setDisplay(fields)
      onChange(
        { mode: 'recognized', evidenceToken: result.evidenceToken, expectedDigest: result.expectedDigest },
        fields
      )
      onReadyChange(true)
    } catch (cause) {
      if (generation.current !== ticket) {
        return
      }
      setError(cause instanceof Error ? cause.message : '识别失败，请重试或明确移除候选')
    }
  }

  const lookupCompany = async () => {
    const code = creditCode.trim().toUpperCase()
    onReadyChange(false)
    onChange({ mode: 'none' })
    setDisplay(undefined)
    if (!/^[0-9A-Z]{18}$/.test(code)) {
      setError('请输入 18 位统一社会信用代码')
      return
    }
    const ticket = ++generation.current
    setResolving(true)
    setError(undefined)
    try {
      const result = await cache.fetchQuery({
        ...trpc.order.subjects.lookupCompany.queryOptions({ creditCode: code }),
        staleTime: 0
      })
      if (generation.current !== ticket) return
      const fields = {
        name: result.company.name,
        number: result.company.unifiedSocialCreditCode,
        legalRepresentative: result.company.legalRepresentative
      }
      setDisplay(fields)
      onChange({ mode: 'registry', creditCode: code, expectedDigest: result.expectedDigest }, fields)
      onReadyChange(true)
    } catch (cause) {
      if (generation.current === ticket) setError(cause instanceof Error ? cause.message : '抓取失败，请重试')
    }
    if (generation.current === ticket) setResolving(false)
  }

  const chooseExisting = async (id: number) => {
    const ticket = ++generation.current
    setResolving(true)
    setError(undefined)
    onReadyChange(false)
    try {
      if (kind === 'company') {
        const row = requireDocument(
          await cache.fetchQuery({ ...trpc.company.reusable.queryOptions({ id }), staleTime: 0 }),
          '企业档案不存在或已不可用'
        )
        if (generation.current !== ticket) {
          return
        }
        const fields = {
          name: row.name,
          number: row.unifiedSocialCreditCode,
          legalRepresentative: row.legalRepresentative
        }
        setDisplay(fields)
        onChange(
          { mode: 'existing', id, expectedUpdatedAt: dayjs(row.updatedAt).toISOString() },
          fields,
          row.legalRepresentativeIdentityId
        )
      } else {
        const row = requireDocument(
          await cache.fetchQuery({ ...trpc.identity.reusable.queryOptions({ id }), staleTime: 0 }),
          '身份档案不存在或已不可用'
        )
        if (generation.current !== ticket) {
          return
        }
        const fields = { name: row.name, number: row.idNumber.replace(/^(.{3}).*(.{4})$/, '$1***********$2') }
        setDisplay(fields)
        onChange({ mode: 'existing', id, expectedUpdatedAt: dayjs(row.updatedAt).toISOString() }, fields)
      }
      onReadyChange(true)
    } catch (cause) {
      if (generation.current === ticket) {
        setError(cause instanceof Error ? cause.message : '读取档案失败')
      }
    }
    if (generation.current === ticket) {
      setResolving(false)
    }
  }

  return (
    <section className="flex min-w-0 flex-col rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
      <div className="mb-4 flex items-start justify-between gap-3 border-b border-slate-100 pb-3 dark:border-slate-800">
        <div>
          <h4 className="m-0 font-semibold">{title}</h4>
          <p className="mt-1 mb-0 text-xs text-slate-500 dark:text-slate-400">
            {kind === 'company' ? '企业名称 · 信用代码 · 法定代表人' : '法人姓名 · 身份证号'}
          </p>
        </div>
        {shownDisplay && (
          <span className="shrink-0 rounded-full bg-teal-50 px-2.5 py-1 text-xs text-teal-700 dark:bg-teal-950/40 dark:text-teal-300">
            {retainingCurrent ? '已保存资料' : '已选资料'}
          </span>
        )}
      </div>
      <Radio.Group
        aria-label={`${title}来源`}
        optionType="button"
        buttonStyle="outline"
        className="flex! flex-wrap self-start"
        value={mode}
        disabled={busy}
        options={[
          { value: 'none', label: current ? '保留当前' : '暂不提供' },
          { value: 'existing', label: '选用已有', disabled: !canRead },
          { value: 'recognized', label: '上传识别', disabled: !canRecognize },
          ...(kind === 'company' ? [{ value: 'registry', label: '代码抓取', disabled: !canLookup }] : [])
        ]}
        onChange={(event) => {
          const nextMode = event.target.value as typeof mode
          generation.current += 1
          setMode(nextMode)
          setDisplay(undefined)
          setFile(undefined)
          setUrl(undefined)
          setError(undefined)
          onChange({ mode: 'none' })
          onReadyChange(nextMode === 'none')
        }}
      />
      <div className="mt-3 min-h-0 space-y-3">
        {mode === 'none' && (
          <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-400 dark:border-slate-700 dark:bg-slate-950">
            {current ? `已关联${title}，本次保留当前资料` : `本次暂不提供${title}`}
          </div>
        )}
        {mode === 'existing' && (
          <Select
            className="w-full"
            aria-label={`搜索已有${title}档案`}
            disabled={busy}
            value={value?.mode === 'existing' ? value.id : undefined}
            placeholder={kind === 'company' ? '搜索已保存的企业名称或信用代码' : '搜索已保存的姓名或身份证号'}
            options={options}
            loading={resolving || companies.isFetching || identities.isFetching}
            labelRender={(option) => shownDisplay?.name ?? option.label ?? `档案 #${option.value}`}
            showSearch={{ filterOption: false, onSearch: setKeyword }}
            onChange={(id: number) => void chooseExisting(id)}
          />
        )}
        {mode === 'registry' && (
          <div className="flex flex-col gap-3">
            <label className="text-sm" htmlFor={creditCodeInputId}>
              统一社会信用代码
            </label>
            <div className="flex flex-wrap gap-2">
              <Input
                id={creditCodeInputId}
                className="min-w-48 flex-1"
                value={creditCode}
                maxLength={18}
                disabled={busy}
                placeholder="输入 18 位信用代码"
                onChange={(event) => {
                  setCreditCode(event.target.value.toUpperCase())
                  setDisplay(undefined)
                  setError(undefined)
                  onChange({ mode: 'none' })
                  onReadyChange(false)
                }}
                onPressEnter={(event) => {
                  event.preventDefault()
                  void lookupCompany()
                }}
              />
              <Button disabled={busy} loading={resolving} onClick={() => void lookupCompany()}>
                抓取信息
              </Button>
            </div>
            <p className="text-content-muted m-0 text-xs leading-5">
              通过企查查查询并回填企业信息，核对后随表单保存。执照原件仍需另行提供。
            </p>
          </div>
        )}
        {mode === 'recognized' && (
          <>
            {url ? <LicenseDocumentPreview src={url} title={title} /> : null}
            <Upload
              accept="image/jpeg,image/png,image/bmp"
              showUploadList={false}
              disabled={busy}
              classNames={{ root: 'block w-full', trigger: 'block! w-full' }}
              beforeUpload={(source) => {
                const parsed = ocrFileSchema.safeParse(source)
                if (!parsed.success) {
                  void message.error(parsed.error.issues[0]?.message)
                  return Upload.LIST_IGNORE
                }
                setFile(source)
                setUrl(URL.createObjectURL(source))
                setDisplay(undefined)
                onChange({ mode: 'none' })
                void recognize(source)
                return false
              }}
            >
              <button
                type="button"
                aria-label={`点击上传${title}`}
                disabled={busy}
                className={`flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 text-slate-500 transition-colors hover:border-teal-500 hover:bg-teal-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400 dark:hover:border-teal-500 dark:hover:bg-slate-900 ${url ? 'h-8 flex-row' : 'h-32 w-full flex-col'}`}
              >
                <UploadCloud aria-hidden size={url ? 16 : 28} strokeWidth={1.5} />
                <span className={url ? 'text-sm' : 'text-sm font-medium'}>{uploadButtonText}</span>
                {!url && <span className="text-xs text-slate-400">上传清晰、完整的证件图片</span>}
              </button>
            </Upload>
            <p className="mt-2 text-xs text-slate-500">JPG / PNG / BMP · 不超过 10 MiB</p>
          </>
        )}
      </div>
      {shownDisplay && (
        <Descriptions
          className="mt-4! border-t border-slate-100 pt-4! dark:border-slate-800"
          size="small"
          column={1}
          items={[
            { key: 'name', label: kind === 'company' ? '企业名称' : '姓名', children: shownDisplay.name || '未识别' },
            {
              key: 'number',
              label: kind === 'company' ? '信用代码' : '身份证号',
              children:
                kind === 'identity'
                  ? shownDisplay.number?.replace(/^(.{3}).*(.{4})$/, '$1***********$2') || '未识别'
                  : shownDisplay.number || '未识别'
            },
            ...(kind === 'company'
              ? [{ key: 'legal', label: '执照法人', children: shownDisplay.legalRepresentative || '未识别' }]
              : [])
          ]}
        />
      )}
      {(error || (mode === 'existing' && (companies.error || identities.error))) && (
        <Alert
          className="mt-3"
          type="error"
          showIcon
          title={error ?? (companies.error || identities.error)?.message}
          action={
            mode === 'recognized' && file ? (
              <Button disabled={busy} onClick={() => void recognize(file)}>
                重新识别
              </Button>
            ) : undefined
          }
        />
      )}
      {(value?.mode === 'recognized' || value?.mode === 'registry') && !error && (
        <p className="mt-auto mb-0 pt-3 text-xs text-slate-500" role="status">
          {value.mode === 'registry' ? '抓取完成' : '识别完成'} · 核对后随表单保存
        </p>
      )}
    </section>
  )
}
